import { Box } from '@chakra-ui/react';
import {
  CloudinaryVideo,
  Grid,
  gridConfig,
  RecommendedBadge,
} from '@time-line/components/common';
import type {
  AspectRatio,
  CloudinaryVideo as CloudinaryVideoType,
} from '@time-line/components/types';
import { ratioToDecimal } from '@time-line/components/utils';
import { BlurredHeroBackgroundImage } from 'components/common/BlurredHeroBackgroundImage';
import { useIsMounted } from 'hooks/useIsMounted';
import type { ReactNode } from 'react';

const { gridBorderRadius } = gridConfig;

interface VideoSplitHeroProps {
  children: ReactNode;
  video: CloudinaryVideoType;
  isBadgeVisible?: boolean;
  direction?: 'ltr' | 'rtl';
  columnWidths?: {
    content: string;
    video: string;
  };
  videoMaxWidth?: number;
  videoAspectRatio?: AspectRatio;
}

export function VideoSplitHero({
  children,
  video,
  isBadgeVisible,
  direction = 'ltr',
  columnWidths,
  videoMaxWidth,
  videoAspectRatio = '4:5',
}: VideoSplitHeroProps) {
  const isMounted = useIsMounted();
  const [contentOrder, videoOrder] = direction === 'ltr' ? [1, 2] : [2, 1];
  const contentSpan = columnWidths?.content || '1 / 6';
  const videoSpan = columnWidths?.video || '7 / -1';

  const aspectRatio = ratioToDecimal(videoAspectRatio);

  return (
    <Grid
      className="section--hero"
      withBorder
      borderRadius={gridBorderRadius}
      overflow="hidden"
      position="relative"
      alignItems="center"
      py={{ md: 'lg' }}
    >
      <Box gridColumn="full" display={{ md: 'none' }}>
        <Box aspectRatio={aspectRatio} />
      </Box>
      <Grid.Inner gridRowGap={0}>
        <BlurredHeroBackgroundImage cloudinaryId={video.cloudinaryId} />
        <Box
          className="hero_content"
          position="relative"
          zIndex={1}
          order={contentOrder}
          alignSelf="center"
          justifyContent="center"
          gridColumn={{
            base: 'span 12',
            md: contentSpan,
          }}
          py={{ base: 'lg', md: 0 }}
          opacity={isMounted ? 1 : 0}
        >
          {children}
        </Box>
        <Box
          className="hero_media"
          display="grid"
          gridColumn={{ base: 'span 12', md: videoSpan }}
          order={{ base: 0, md: videoOrder }}
          justifyItems="center"
          alignSelf={{ md: 'center' }}
        >
          <Box
            position={{ base: 'absolute', md: 'relative' }}
            top={0}
            left={0}
            right={0}
            borderRadius={{ md: '3xl' }}
            overflow="hidden"
            maxW={{ md: videoMaxWidth }}
            w="full"
          >
            <CloudinaryVideo
              title={video.title}
              cloudinaryId={video.cloudinaryId}
              aspectRatio={videoAspectRatio}
            />
            {isBadgeVisible && (
              <Box
                position="absolute"
                top={{ base: 'sm', md: 'lg' }}
                left="0"
                whiteSpace="nowrap"
              >
                <RecommendedBadge size="md" borderLeftRadius="0" />
              </Box>
            )}
          </Box>
        </Box>
      </Grid.Inner>
    </Grid>
  );
}
